import { CommunitySubscriptionInfo } from './community';
import { User } from './user';

export enum SubscriptionStatus {
  ACTIVE = 'ACTIVE',
  CANCELLED = 'CANCELLED',
  APPROVAL_PENDING = 'APPROVAL_PENDING',
  MIGRATED = 'MIGRATED',
  TRIAL = 'TRIAL',
  /** Yearly subscription created; payment via Stripe invoice / hosted page pending. */
  INVOICE_PENDING = 'INVOICE_PENDING',
}

export enum SubscriptionFrequency {
  YEAR = 'YEAR',
  MONTH = 'MONTH',
}

type SubscriptionCommon = {
  id: string;
  chatEnabled: boolean;
  currency: string;
  maxCommunities: number;
  maxGroups: number;
  maxMembers: number;
  maxStorage: number;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
};

export type SubscriptionPlan = SubscriptionCommon & {
  description: string | null;
  name: string;
  productName: string;
  monthlyPlanId: string;
  yearlyPlanId: string;
  monthlyValue: number;
  yearlyValue: number;
  productId: string;
  tax: number;
};

/** One-time onboarding SKU from GET `subscriptions/plans/v2` (`onboarding` array). */
export type OnboardingCatalogItem = {
  id: string;
  slug: string;
  productName: string;
  currency: string;
  oneTimeValue: number | null;
  stripePriceIdOneTime: string | null;
  stripeProductId: string | null;
  sortOrder: number;
};

/** Paid onboarding row from GET `subscriptions?new=true`. */
export type OnboardingPurchaseSummary = {
  id: string;
  onboardingPlanId: string;
  /** Matches Stripe `session_id` on return URL - for post-checkout UI. */
  stripeCheckoutSessionId?: string | null;
  slug: string | null;
  productName: string | null;
  amountTotal: number | null;
  currency: string | null;
  createdAt: string;
  updatedAt: string;
};

/** POST/GET `billingAddress` for CHF invoice subscriptions. */
export type InvoiceBillingAddressPayload = {
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  address?: {
    line1?: string | null;
    line2?: string | null;
    city?: string | null;
    state?: string | null;
    postal_code?: string | null;
    country?: string | null;
  } | null;
};

/** POST/GET `taxId` for CHF invoice subscriptions. */
export type InvoiceTaxIdPayload = {
  businessName?: string | null;
  taxId?: string | null;
  taxIdType?: string | null;
  externalTaxIdType?: string | null;
};

export type Subscription = SubscriptionCommon & {
  status: SubscriptionStatus;
  frequency: SubscriptionFrequency;
  planId: string;
  subscriptionId: string | null;
  startDate: string;
  endDate: string;
  subscriptionPlanId: string;
  userId: string;
  /** Finance / third-party payer emails (e.g. invoice billing); optional until backend sets them. */
  payerEmails?: string[] | null;
  billingAddress?: InvoiceBillingAddressPayload | null;
  taxId?: InvoiceTaxIdPayload | null;
  enterpriseInvoiceRequestId?: string | null;
};

export type EnterpriseInvoiceRequestSummary = {
  id: string;
  contactEmail: string;
  organizationName?: string | null;
};

export type UserSubscription = Subscription & {
  info: CommunitySubscriptionInfo;
  subscriptionPlan: SubscriptionPlan;
  user: User;
  /** Optional: returned by GET subscriptions when billing integration exposes them. */
  paymentMethodBrand?: string | null;
  paymentMethodLast4?: string | null;
  paymentMethodExpMonth?: number | null;
  paymentMethodExpYear?: number | null;
  billingPortalUrl?: string | null;
  /** Hosted Stripe invoice page for the current overdue invoice. */
  overdueInvoiceUrl?: string | null;
  lastInvoiceAmount?: number | null;
  lastInvoiceCurrency?: string | null;
  lastInvoicePaidAt?: string | null;
  lastInvoicePdfUrl?: string | null;
  stripePayment?: any | null;
  proratedValue?: number | null;
  stripeCollectionMethod?: 'charge_automatically' | 'send_invoice' | null;
  stripeCurrentPeriodEnd?: string | null;
  stripePriceUnitAmount?: number | null;
  stripePriceCurrency?: string | null;
  stripePriceInterval?: 'day' | 'week' | 'month' | 'year' | null;
  /** Stripe `Subscription.status` when loaded via billing snapshot (`sub_…`). */
  stripeSubscriptionStatus?: string | null;
  stripeCancelAtPeriodEnd?: boolean | null;
  stripeCancelAt?: string | null;
  stripeCanceledAt?: string | null;
  stripeEndedAt?: string | null;
  enterpriseInvoiceRequest?: EnterpriseInvoiceRequestSummary | null;
  onboardingPurchases?: OnboardingPurchaseSummary[];
};
